import * as React from 'react';
import { Theme, useMediaQuery } from '@mui/material';
import { ThemeProvider, useColorScheme } from '@mui/material/styles';
import InitColorSchemeScript from '@mui/material/InitColorSchemeScript';
import CssBaseline from '@mui/material/CssBaseline';
import { PaletteModeContext } from '../shared/context';

const COLOR_SCHEME_ATTRIBUTE = 'data-toolpad-color-scheme';
const COLOR_SCHEME_STORAGE_KEY = 'toolpad-color-scheme';
const MODE_STORAGE_KEY = 'toolpad-mode';

function usePreferredMode(window?: Window) {
  const prefersDarkMode = useMediaQuery(
    '(prefers-color-scheme: dark)',
    window && {
      matchMedia: window.matchMedia,
    },
  );
  return prefersDarkMode
    ? 'dark'
    : 'light';
}

interface CssVarsPaletteModeProviderProps {
  children: React.ReactNode;
  window?: Window;
}

function CssVarsPaletteModeProvider(props: CssVarsPaletteModeProviderProps) {
  const { children, window: appWindow } = props;

  const preferredMode = usePreferredMode(appWindow);
  const { mode, setMode, allColorSchemes } = useColorScheme();

  // The v6 API, based on `useColorScheme`
  const paletteModeContextValue = React.useMemo(() => {
    return {
      paletteMode: !mode || mode === 'system'
        ? preferredMode
        : mode,
      setPaletteMode: setMode,
      isDualTheme: allColorSchemes.length > 1,
    };
  }, [allColorSchemes, mode, preferredMode, setMode]);

  return (
    <PaletteModeContext.Provider value={paletteModeContextValue}>
      {children}
    </PaletteModeContext.Provider>
  );
}

interface AppThemeProviderProps {
  children: React.ReactNode;
  theme: Theme;
  window?: Window;
}

/**
 * @ignore - internal component.
 */
function AppThemeProvider(props: AppThemeProviderProps) {
  const { children, theme, window: appWindow } = props;

  return (
    <ThemeProvider
      theme={theme}
      documentNode={appWindow?.document}
      colorSchemeStorageKey={COLOR_SCHEME_STORAGE_KEY}
      modeStorageKey={MODE_STORAGE_KEY}
    >
      <InitColorSchemeScript
        attribute={COLOR_SCHEME_ATTRIBUTE}
        colorSchemeStorageKey={COLOR_SCHEME_STORAGE_KEY}
        modeStorageKey={MODE_STORAGE_KEY}
      />
      <CssVarsPaletteModeProvider window={appWindow}>
        <CssBaseline enableColorScheme />
        {children}
      </CssVarsPaletteModeProvider>
    </ThemeProvider>
  );
}

export { AppThemeProvider };
